<template>
  <div class="sales-volume-thresholds-inline">
    <div class="sv-inline-title">
      <i class="cog icon"></i>
      <span>Adjust Threshold — {{ productTitle }} · {{ marketplaceLabel }}</span>
    </div>
    <div class="sv-inline-fields">
      <div class="sv-inline-field">
        <label>No-sales alert after</label>
        <input
          v-model.number="form.noSalesHours"
          type="number"
          min="1"
          class="sv-inline-input"
          :class="{ 'has-value': form.noSalesHours != null && form.noSalesHours !== '' }"
          placeholder="72"
          :disabled="!resolvedProductId"
        />
        <span class="sv-field-unit">hours</span>
      </div>
      <div class="sv-inline-divider" />
      <div class="sv-inline-field">
        <label>Drop alert</label>
        <input
          v-model.number="form.rollingDrop"
          type="number"
          min="1"
          max="100"
          class="sv-inline-input"
          :class="{ 'has-value': form.rollingDrop != null && form.rollingDrop !== '' }"
          placeholder="—"
          :disabled="!resolvedProductId"
        />
        <span class="sv-field-unit">% vs avg</span>
      </div>
      <div class="sv-inline-field">
        <label>Avg over</label>
        <input
          v-model.number="form.rollingDays"
          type="number"
          min="1"
          class="sv-inline-input"
          :class="{ 'has-value': form.rollingDays != null && form.rollingDays !== '' }"
          placeholder="14"
          :disabled="!resolvedProductId"
        />
        <span class="sv-field-unit">days</span>
      </div>
    </div>
    <div class="sv-inline-actions">
      <button type="button" class="sv-inline-btn-save" :disabled="saving || !resolvedProductId" @click="save">Save</button>
      <button type="button" class="sv-inline-btn-cancel" @click="$emit('cancel')">Cancel</button>
      <span class="sv-inline-note">Or configure all products via <strong>Configure Thresholds</strong> above.</span>
    </div>
    <div v-if="saveError" class="sv-inline-error">{{ saveError }}</div>
    <div v-if="saving" class="sv-inline-loading"><i class="notched circle loading icon"></i> Saving…</div>
  </div>
</template>

<script>
import {
  extractSalesVolumeListItems,
  productIdFromSalesVolumeRow,
  salesVolumeHttpResponseEntity,
  salesVolumeListingIdFromProduct,
  formFieldsFromSalesVolumeSettings
} from '../../utils/salesVolumeAlertThresholdsApiParse.js'

function numVal (v) {
  if (v === undefined || v === null || v === '') return null
  var n = Number(v)
  return !isNaN(n) && n >= 0 ? n : null
}

function formFromItem (item) {
  var s = (item && item.settings) ? item.settings : {}
  return {
    noSalesHours: numVal(s.no_sales_hours != null ? s.no_sales_hours : s.noSalesHours),
    dailyFloor: numVal(s.daily_sales_floor != null ? s.daily_sales_floor : s.dailyFloor),
    dailyCeiling: numVal(s.daily_sales_ceiling != null ? s.daily_sales_ceiling : s.dailyCeiling),
    rollingDrop: numVal(s.rolling_drop_percent != null ? s.rolling_drop_percent : s.rollingDropPercent),
    rollingSpike: numVal(s.rolling_spike_percent != null ? s.rolling_spike_percent : s.rollingSpikePercent),
    rollingDays: numVal(s.rolling_avg_days != null ? s.rolling_avg_days : s.rollingAvgDays),
    rollingMinUnits: numVal(s.rolling_min_units != null ? s.rolling_min_units : s.rollingMinUnits),
    bsrAbove: numVal(s.bsr_alert_above != null ? s.bsr_alert_above : s.bsrAlertAbove)
  }
}

export default {
  name: 'SalesVolumeThresholdsInline',
  props: {
    item: { type: Object, required: true },
    productId: { type: [String, Number], default: null },
    sellerAccountId: { type: String, default: null },
    productTitle: { type: String, default: '' },
    marketplace: { type: String, default: '' },
    salesVolumeThresholdsApiBase: { type: String, default: '/api/v1/sales-volume-alert-thresholds' }
  },
  data: function () {
    return {
      form: formFromItem(null),
      saving: false,
      saveError: null
    }
  },
  computed: {
    marketplaceLabel: function () {
      if (!this.marketplace) return '—'
      var m = String(this.marketplace).toUpperCase()
      return m === 'GB' ? 'UK' : m
    },
    resolvedProductId: function () {
      if (this.productId != null && this.productId !== '') return String(this.productId)
      return this.item ? salesVolumeListingIdFromProduct(this.item) : ''
    }
  },
  watch: {
    item: {
      handler: function () {
        this.form = formFromItem(this.item)
      },
      immediate: true
    },
    productId: function () {
      this.form = formFromItem(this.item)
      this.hydrateFromApiSilent()
    }
  },
  mounted: function () {
    this.hydrateFromApiSilent()
  },
  methods: {
    httpParams: function () {
      var p = {}
      if (this.sellerAccountId) p.seller_account_id = this.sellerAccountId
      return p
    },
    hydrateFromApiSilent: function () {
      var pid = this.resolvedProductId
      if (!pid) return
      var self = this
      var base = this.salesVolumeThresholdsApiBase
      var opts = function (extra) {
        return {
          params: Object.assign({
            product_ids: pid,
            _skcb: Date.now()
          }, extra || {}),
          headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
          timeout: 45000
        }
      }
      var apply = function (res) {
        var items = extractSalesVolumeListItems(salesVolumeHttpResponseEntity(res))
        var i
        for (i = 0; i < items.length; i++) {
          var row = items[i]
          if (productIdFromSalesVolumeRow(row) !== pid) continue
          var raw = row.settings
          if (raw == null) raw = {}
          else if (typeof raw === 'string') {
            try {
              raw = JSON.parse(raw)
            } catch (e) {
              raw = {}
            }
          }
          var settings = typeof raw === 'object' && raw !== null ? raw : {}
          self.form = formFieldsFromSalesVolumeSettings(settings)
          return
        }
      }
      this.$http.get(base, opts()).then(function (res) {
        apply(res)
      }).catch(function (res) {
        if (res && res.status === 304) {
          return self.$http.get(base, opts({ _skretry: Date.now() })).then(function (res2) {
            apply(res2)
          })
        }
      })
    },
    applySavedPayloadToItem: function (settings) {
      var item = this.item
      if (!item) return
      if (!item.settings) this.$set(item, 'settings', {})
      var s = settings
      if (Object.prototype.hasOwnProperty.call(s, 'no_sales_hours')) {
        this.$set(item.settings, 'no_sales_hours', s.no_sales_hours)
        this.$set(item.settings, 'noSalesHours', s.no_sales_hours)
      }
      if (Object.prototype.hasOwnProperty.call(s, 'rolling_drop_percent')) {
        this.$set(item.settings, 'rolling_drop_percent', s.rolling_drop_percent)
        this.$set(item.settings, 'rollingDropPercent', s.rolling_drop_percent)
      }
      if (Object.prototype.hasOwnProperty.call(s, 'rolling_avg_days')) {
        this.$set(item.settings, 'rolling_avg_days', s.rolling_avg_days)
        this.$set(item.settings, 'rollingAvgDays', s.rolling_avg_days)
      }
    },
    /** Only fields shown in this inline editor — avoids sending null for floor/ceiling/BSR etc. */
    inlineSettingsPayload: function () {
      function part (v) {
        if (v === null || v === undefined || v === '') return null
        var n = Number(v)
        return isNaN(n) ? null : n
      }
      return {
        no_sales_hours: part(this.form.noSalesHours),
        rolling_drop_percent: part(this.form.rollingDrop),
        rolling_avg_days: part(this.form.rollingDays)
      }
    },
    save: function () {
      var pid = this.resolvedProductId
      if (!pid) return
      this.saving = true
      this.saveError = null
      var settings = this.inlineSettingsPayload()
      var body = { updates: [{ product_id: pid, settings: settings }] }
      var self = this
      this.$http.patch(this.salesVolumeThresholdsApiBase, body, { params: this.httpParams(), timeout: 60000 }).then(function (res) {
        self.saving = false
        var failed = res.body && res.body.failed
        if (failed > 0) {
          self.saveError = 'Some updates failed. Check results from the server.'
          return
        }
        self.applySavedPayloadToItem(settings)
        self.$emit('saved')
      }, function (err) {
        self.saving = false
        self.saveError = (err.body && err.body.message) || err.statusText || 'Failed to save settings'
      })
    }
  }
}
</script>

<style scoped>
.sales-volume-thresholds-inline {
  padding: 16px 20px 16px 20px;
  border-top: 1px solid var(--border-light);
  background: #f8faff;
}

.sv-inline-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--primary-navy-dark, #143a66);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.sv-inline-title .icon {
  margin: 0;
}

.sv-inline-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}

.sv-inline-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sv-inline-field label {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.sv-inline-input {
  width: 88px;
  padding: 6px 8px;
  border: 1px solid var(--border-light);
  border-radius: 6px;
  font-size: 13px;
  text-align: center;
  font-family: inherit;
  color: var(--text-primary);
  background: var(--bg-white);
}

.sv-inline-input:focus {
  outline: none;
  border-color: var(--primary-navy-dark, #143a66);
  box-shadow: 0 0 0 2px rgba(27, 75, 130, 0.1);
}

.sv-inline-input.has-value {
  border-color: var(--primary-navy-light, #2a5f9e);
  background: #eff4fb;
  font-weight: 600;
}

.sv-field-unit {
  font-size: 10px;
  color: var(--text-tertiary);
  text-align: center;
  margin-top: 2px;
}

.sv-inline-divider {
  width: 1px;
  height: 48px;
  background: var(--border-light);
  align-self: center;
}

.sv-inline-actions {
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.sv-inline-btn-save {
  padding: 8px 20px;
  background: var(--accent-orange);
  border: none;
  border-radius: 7px;
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.sv-inline-btn-save:hover:not(:disabled) {
  background: var(--accent-orange-hover);
}

.sv-inline-btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sv-inline-btn-cancel {
  padding: 8px 16px;
  background: none;
  border: 1px solid var(--border-medium);
  border-radius: 7px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  color: var(--text-secondary);
}

.sv-inline-note {
  font-size: 11px;
  color: var(--text-tertiary);
}

.sv-inline-error {
  margin-top: 8px;
  color: var(--status-critical);
  font-size: 13px;
}

.sv-inline-loading {
  margin-top: 8px;
  color: var(--text-secondary);
  font-size: 13px;
}
</style>



// WEBPACK FOOTER //
// SalesVolumeThresholdsInline.vue?7c1d28c8